import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { roleApi, Role, Module, Action } from '@/services/roleApi';
import { PermissionGuard } from '@/components/Auth/PermissionGuard';
import Pagination from '@/components/Common/Pagination';

const RolesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const queryClient = useQueryClient();

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles', currentPage, search, showActiveOnly],
    queryFn: () => roleApi.getAll({
      page: currentPage,
      limit,
      search: search || undefined,
      isActive: showActiveOnly ? true : undefined,
    }).then(res => res.data.data),
  });

  const { data: permissionsMeta } = useQuery({
    queryKey: ['permissions-meta'],
    queryFn: () => roleApi.getPermissionsMeta().then(res => res.data.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => roleApi.delete(id),
    onSuccess: () => {
      toast.success('Role deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to delete role'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (role: Role) => roleApi.update(role._id, { isActive: !role.isActive }),
    onSuccess: () => {
      toast.success('Role status updated');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update role'),
  });

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setShowEditModal(true);
  };

  const handleViewPermissions = (role: Role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const handleDelete = (role: Role) => {
    if (window.confirm(`Are you sure you want to delete the "${role.name}" role? This action cannot be undone.`)) {
      deleteMutation.mutate(role._id);
    }
  };

  const handleToggleStatus = (role: Role) => {
    toggleStatusMutation.mutate(role);
  };

  const getStatusBadge = (role: Role) => {
    if (!role.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
    if (role.isSystem) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <CogIcon className="w-3 h-3 mr-1" />
          System
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Roles & Permissions</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage user roles and their permissions</p>
        </div>
        <PermissionGuard module="roles" action="create">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Role
          </button>
        </PermissionGuard>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search roles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="activeOnly"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="activeOnly" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Active only
            </label>
          </div>
        </div>
      </div>

      {/* Roles Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading roles...</p>
          </div>
        ) : !rolesData?.roles?.length ? (
          <div className="p-8 text-center text-gray-500">
            <ShieldCheckIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
            <p>No roles found</p>
            <PermissionGuard module="roles" action="create">
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-blue-600 hover:text-blue-800"
              >
                Create your first role
              </button>
            </PermissionGuard>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {rolesData.roles.map((role: Role) => (
                  <tr key={role._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {role.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            by {role.createdBy?.name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {role.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {role.permissions.length} modules
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(role.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewPermissions(role)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View permissions"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {!role.isSystem && (
                          <>
                            <PermissionGuard module="roles" action="update">
                              <button
                                onClick={() => handleEdit(role)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit role"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(role)}
                                className={role.isActive ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                                title={role.isActive ? "Deactivate" : "Activate"}
                              >
                                {role.isActive ? <XCircleIcon className="h-4 w-4" /> : <CheckCircleIcon className="h-4 w-4" />}
                              </button>
                            </PermissionGuard>
                            <PermissionGuard module="roles" action="delete">
                              <button
                                onClick={() => handleDelete(role)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete role"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {rolesData?.pagination && rolesData.pagination.pages > 1 && (
          <div className="px-6 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={rolesData.pagination.pages}
              onPageChange={setCurrentPage}
              totalItems={rolesData.pagination.total}
              itemsPerPage={limit}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          modules={permissionsMeta?.modules || []}
          actions={permissionsMeta?.actions || []}
        />
      )}

      {showEditModal && selectedRole && (
        <EditRoleModal
          role={selectedRole}
          onClose={() => setShowEditModal(false)}
          modules={permissionsMeta?.modules || []}
          actions={permissionsMeta?.actions || []}
        />
      )}

      {showPermissionsModal && selectedRole && (
        <ViewPermissionsModal
          role={selectedRole}
          onClose={() => setShowPermissionsModal(false)}
          modules={permissionsMeta?.modules || []}
          actions={permissionsMeta?.actions || []}
        />
      )}
    </div>
  );
};

// Create Role Modal Component
const CreateRoleModal: React.FC<{
  onClose: () => void;
  modules: Module[];
  actions: Action[];
}> = ({ onClose, modules, actions }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<{ module: string; actions: string[] }[]>([]);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string; permissions: typeof permissions }) =>
      roleApi.create(data),
    onSuccess: () => {
      toast.success('Role created successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to create role'),
  });

  const handlePermissionChange = (module: string, action: string, checked: boolean) => {
    setPermissions(prev => {
      const existing = prev.find(p => p.module === module);
      if (existing) {
        if (checked) {
          return prev.map(p => 
            p.module === module 
              ? { ...p, actions: [...new Set([...p.actions, action])] }
              : p
          );
        } else {
          const updated = { ...existing, actions: existing.actions.filter(a => a !== action) };
          return updated.actions.length > 0 
            ? prev.map(p => p.module === module ? updated : p)
            : prev.filter(p => p.module !== module);
        }
      } else if (checked) {
        return [...prev, { module, actions: [action] }];
      }
      return prev;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({ name: name.trim(), description: description.trim(), permissions });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Create Role</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={50}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={200}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
            <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
              {modules.map(module => (
                <div key={module.name} className="mb-3">
                  <div className="font-medium text-sm text-gray-900 dark:text-white mb-1">
                    {module.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {module.description}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {module.actions.map(action => {
                      const actionInfo = actions.find(a => a.name === action);
                      const isChecked = permissions.some(p => 
                        p.module === module.name && p.actions.includes(action)
                      );
                      return (
                        <label key={action} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handlePermissionChange(module.name, action, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                          />
                          {actionInfo?.label || action}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || !name.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Role Modal Component
const EditRoleModal: React.FC<{
  role: Role;
  onClose: () => void;
  modules: Module[];
  actions: Action[];
}> = ({ role, onClose, modules, actions }) => {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || '');
  const [permissions, setPermissions] = useState(role.permissions);
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; description: string; permissions: typeof permissions }) =>
      roleApi.update(role._id, data),
    onSuccess: () => {
      toast.success('Role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update role'),
  });

  const handlePermissionChange = (module: string, action: string, checked: boolean) => {
    setPermissions(prev => {
      const existing = prev.find(p => p.module === module);
      if (existing) {
        if (checked) {
          return prev.map(p => 
            p.module === module 
              ? { ...p, actions: [...new Set([...p.actions, action])] }
              : p
          );
        } else {
          const updated = { ...existing, actions: existing.actions.filter(a => a !== action) };
          return updated.actions.length > 0 
            ? prev.map(p => p.module === module ? updated : p)
            : prev.filter(p => p.module !== module);
        }
      } else if (checked) {
        return [...prev, { module, actions: [action] }];
      }
      return prev;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    updateMutation.mutate({ name: name.trim(), description: description.trim(), permissions });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Role</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={50}
              disabled={role.isSystem}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
            {role.isSystem && (
              <p className="text-xs text-gray-500 mt-1">System role names cannot be changed</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={200}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {!role.isSystem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
              <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
                {modules.map(module => (
                  <div key={module.name} className="mb-3">
                    <div className="font-medium text-sm text-gray-900 dark:text-white mb-1">
                      {module.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {module.description}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {module.actions.map(action => {
                        const actionInfo = actions.find(a => a.name === action);
                        const isChecked = permissions.some(p => 
                          p.module === module.name && p.actions.includes(action)
                        );
                        return (
                          <label key={action} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handlePermissionChange(module.name, action, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                            />
                            {actionInfo?.label || action}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading || !name.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isLoading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Permissions Modal Component
const ViewPermissionsModal: React.FC<{
  role: Role;
  onClose: () => void;
  modules: Module[];
  actions: Action[];
}> = ({ role, onClose, modules, actions }) => {
  const getActionLabel = (actionName: string) => {
    return actions.find(a => a.name === actionName)?.label || actionName;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] max-h-[80vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Permissions: {role.name}
        </h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">Role Details</h4>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Description:</strong> {role.description || 'No description'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                <strong>Type:</strong> {role.isSystem ? 'System Role' : 'Custom Role'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                <strong>Status:</strong> {role.isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-sm text-gray-900 dark:text-white mb-2">Module Permissions</h4>
            <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
              {modules.map(module => {
                const modulePermissions = role.permissions.find(p => p.module === module.name);
                return (
                  <div key={module.name} className="mb-3 pb-3 border-b border-gray-200 last:border-0">
                    <div className="font-medium text-sm text-gray-900 dark:text-white mb-1">
                      {module.label}
                    </div>
                    {modulePermissions ? (
                      <div className="flex flex-wrap gap-2">
                        {modulePermissions.actions.map(action => (
                          <span
                            key={action}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {getActionLabel(action)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-400">No permissions</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
